<template>
  <div class="login-outer">
    <div class="login-page">
      <div class="left-panel">
        <div class="brand-section">
          <img src="../assets/logo.png" alt="Logo" class="logo">
          <h1 class="brand-title">有戏后台</h1>
          <p class="brand-desc">打造独立自主的流量广告变现王国</p>
        </div>
      </div>

      <div class="right-panel">
        <div class="login-wrapper">
          <div class="login-header">
            <h2>欢迎登录</h2>
            <p>请输入您的账号和密码</p>
          </div>
          <el-form ref="loginForm" :model="loginForm" :rules="loginRules" label-width="0px">
            <el-form-item prop="username">
              <el-input
                v-model="loginForm.username"
                placeholder="请输入账号"
                prefix-icon="el-icon-user"
                @keyup.enter.native="handleLogin"
              />
            </el-form-item>
            <el-form-item prop="password">
              <el-input
                v-model="loginForm.password"
                type="password"
                placeholder="请输入密码"
                prefix-icon="el-icon-lock"
                show-password
                @keyup.enter.native="handleLogin"
              />
            </el-form-item>
            <div class="form-extra">
              <el-checkbox v-model="rememberMe">记住账号</el-checkbox>
              <a href="#" @click.prevent="showForgotPassword">忘记密码</a>
            </div>
            <el-form-item>
              <el-button
                type="primary"
                class="login-btn"
                @click="handleLogin"
                :loading="loading"
              >
                登 录
              </el-button>
            </el-form-item>
          </el-form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { resetRedirectFlag } from '@/utils/http.js'

/**
 * 解码 JWT token 的 payload 部分（不验证签名）
 * @param {string} token - JWT token 字符串
 * @returns {object|null} 解码后的 payload 对象
 */
function parseJwtPayload(token) {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(json)
  } catch (e) {
    return null
  }
}

export default {
  name: 'LoginPage',
  data() {
    return {
      loginForm: {
        username: '',
        password: ''
      },
      loginRules: {
        username: [
          { required: true, message: '请输入用户名', trigger: 'blur' }
        ],
        password: [
          { required: true, message: '请输入密码', trigger: 'blur' }
        ]
      },
      loading: false,
      rememberMe: false
    }
  },
  methods: {
    async handleLogin() {
      this.$refs.loginForm.validate(async valid => {
        if (valid) {
          this.loading = true;
          try {
            const response = await fetch('/admin/login', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                username: this.loginForm.username,
                password: this.loginForm.password
              })
            });

            const result = await response.json();

            if(result.code === 200 && result.data && result.data.token) {
              const token = result.data.token
              localStorage.setItem('token', token)

              const payload = parseJwtPayload(token)
              if (!payload) {
                localStorage.removeItem('token')
                this.$message.error('解析登录信息失败')
                this.loading = false
                return
              }

              if (payload.status === 1) {
                localStorage.removeItem('token')
                this.$message.error('该账号已被禁用，请联系超级管理员')
                this.loading = false
                return
              }

              const roleVal = parseInt(payload.role, 10)
              const statusVal = parseInt(payload.status, 10)

              localStorage.setItem('role', !isNaN(roleVal) ? roleVal : 0)
              localStorage.setItem('status', !isNaN(statusVal) ? statusVal : 0)
              localStorage.setItem('username', payload.username || this.loginForm.username)
              const userId = payload.adminid || payload.adminId || result.data.id || result.data.userId || payload.id || payload.userId || payload.sub || ''
              localStorage.setItem('userId', userId)

              if (this.rememberMe) {
                localStorage.setItem('rememberedUsername', this.loginForm.username);
              } else {
                localStorage.removeItem('rememberedUsername');
              }

              resetRedirectFlag()
              this.$router.push('/ad-revenue')
              this.$message.success('登录成功')
            } else {
              this.$message.error(result.msg || '登录失败：未返回有效数据');
            }
          } catch (error) {
            this.$message.error(error.message || '登录失败');
          } finally {
            this.loading = false;
          }
        }
      });
    },
    showForgotPassword() {
      this.$message.info('请联系系统管理员重置密码');
    }
  },
  mounted() {
    const rememberedUsername = localStorage.getItem('rememberedUsername');
    if (rememberedUsername) {
      this.loginForm.username = rememberedUsername;
      this.rememberMe = true;
    }
  }
}
</script>

<style scoped>
.login-outer {
  width: 100vw;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background:
    radial-gradient(ellipse at 20% 20%, rgba(64, 158, 255, 0.08) 0%, transparent 50%),
    radial-gradient(ellipse at 80% 80%, rgba(31, 45, 61, 0.06) 0%, transparent 45%),
    linear-gradient(160deg, #eef1f6 0%, #f5f7fa 50%, #e8ecf1 100%);
}

.login-page {
  display: flex;
  width: min(920px, 92vw);
  height: min(560px, 80vh);
  overflow: hidden;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md), 0 0 0 1px rgba(15, 23, 42, 0.04);
  background: #fff;
}

.left-panel {
  flex: 0 0 42%;
  background: linear-gradient(165deg, #1a2533 0%, #1f2d3d 45%, #2a3f55 100%);
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 48px 36px;
}

.left-panel::before {
  content: '';
  position: absolute;
  top: -40%;
  right: -30%;
  width: 90%;
  height: 90%;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(64, 158, 255, 0.18) 0%, transparent 70%);
  pointer-events: none;
}

.left-panel::after {
  content: '';
  position: absolute;
  bottom: -20%;
  left: -20%;
  width: 70%;
  height: 70%;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.04) 0%, transparent 70%);
  pointer-events: none;
}

.brand-section {
  position: relative;
  z-index: 1;
  text-align: center;
  color: #ffffff;
}

.logo {
  width: 64px;
  height: 64px;
  margin-bottom: 20px;
  filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.25));
}

.brand-title {
  font-size: 1.65rem;
  font-weight: 700;
  margin: 0 0 10px;
  letter-spacing: 1px;
}

.brand-desc {
  font-size: 0.9rem;
  opacity: 0.72;
  margin: 0;
  letter-spacing: 0.5px;
  line-height: 1.5;
}

.right-panel {
  flex: 1;
  background: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 48px;
}

.login-wrapper {
  width: 100%;
  max-width: 360px;
  animation: fadeInUp 0.45s ease-out;
}

.login-header {
  margin-bottom: 32px;
}

.login-header h2 {
  margin: 0 0 8px;
  font-size: 24px;
  font-weight: 700;
  color: var(--color-text);
}

.login-header p {
  margin: 0;
  font-size: 14px;
  color: var(--color-text-muted);
}

.form-extra {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.form-extra a {
  font-size: 13px;
  color: var(--color-primary);
  text-decoration: none;
}
.form-extra a:hover {
  color: var(--color-primary-hover);
}

.login-btn {
  width: 100%;
  height: 44px;
  border-radius: var(--radius-sm);
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 4px;
  background: var(--color-primary) !important;
  border: none !important;
  transition: background 0.2s, box-shadow 0.2s;
}
.login-btn:hover,
.login-btn:focus {
  background: var(--color-primary-hover) !important;
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.35);
}

.login-page ::v-deep .el-input__inner {
  border-radius: var(--radius-sm);
  height: 44px;
  line-height: 44px;
  border: 1px solid var(--color-border);
  background: #fafbfc;
  transition: border-color 0.2s, background 0.2s;
}
.login-page ::v-deep .el-input__inner:focus {
  border-color: var(--color-primary);
  background: #fff;
}
.login-page ::v-deep .el-input__prefix {
  left: 10px;
  color: var(--color-text-muted);
}
.login-page ::v-deep .el-input--prefix .el-input__inner {
  padding-left: 36px;
}
.login-page ::v-deep .el-checkbox__label {
  font-size: 13px;
  color: var(--color-text-secondary);
}
.login-page ::v-deep .el-checkbox__input.is-checked .el-checkbox__inner {
  background-color: var(--color-primary);
  border-color: var(--color-primary);
}
.login-page ::v-deep .el-checkbox__input.is-checked + .el-checkbox__label {
  color: var(--color-primary);
}
.login-page ::v-deep .el-form-item {
  margin-bottom: 20px;
}

@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
}

@media screen and (max-width: 768px) {
  .login-outer {
    background: #f5f7fa;
    align-items: stretch;
  }
  .login-page {
    width: 100vw;
    height: 100vh;
    border-radius: 0;
    box-shadow: none;
    flex-direction: column;
  }
  .left-panel {
    flex: none;
    padding: 36px 24px;
    min-height: auto;
  }
  .brand-title {
    font-size: 1.35rem;
  }
  .right-panel {
    flex: 1;
    padding: 28px 20px;
    align-items: flex-start;
  }
  .login-wrapper {
    max-width: 100%;
  }
}

@media screen and (max-width: 480px) {
  .left-panel {
    padding: 28px 16px;
  }
  .logo {
    width: 48px;
    height: 48px;
    margin-bottom: 12px;
  }
  .brand-title {
    font-size: 1.2rem;
  }
  .brand-desc {
    font-size: 0.8rem;
  }
  .login-header h2 {
    font-size: 20px;
  }
}
</style>
